import React, { useState, useEffect, useRef } from 'react';
import Modal from 'react-modal';
import Input from "../components/forms/Input";
import axios from "axios";
import {toast} from "react-toastify";
import {format, parse, startOfDay, endOfDay} from "date-fns";
import {DATE_FORMATS} from "../constants/DateFormats";
import {doubleBookedHelper} from "../helpers/DoubleBookedHelper";

const CreateEditUserLeaveRequestModal = (props) => {
    const {
        userId              = null,
        userLeaveRequestId  = null,
        show                = false,
        parentId            = "",
        disabled            = false,
        onCancel            = () => {},
        onConfirm           = () => {},
        id                  = "create-edit-user-leave-request-modal",
    } = props;

    const [submitting, setSubmitting]       = useState(false);
    const [leaveReasons, setLeaveReasons]   = useState([]);
    const [errors, setErrors]               = useState(null);

    const [startDate, setStartDate]         = useState("");
    const [endDate, setEndDate]             = useState("");
    const [leaveReason, setLeaveReason]     = useState("");

    useEffect(() => {
        if (!!show) {
            load();
        }
    }, [show]);

    const load = () => {
        axios.post(route('web-api.user-leave-request.config').toString(), {
            user_leave_request_id: userLeaveRequestId
        })
            .then((response) => {
                if (!!response && !!response.data) {
                    if (!!response.data.leave_reasons) {
                        setLeaveReasons(response.data.leave_reasons);
                    }

                    if (!!response.data.user_leave_request) {
                        setStartDate(response.data.user_leave_request.start_at);
                        setEndDate(response.data.user_leave_request.end_at);
                        setLeaveReason(response.data.user_leave_request.leave_reason_id);
                    } else if (!!response.data.default_leave_reason_id) {
                        setLeaveReason(response.data.default_leave_reason_id);
                    }
                }
            })
            .catch((error) => {
                console.log(error);
                toast.error(<div>{"An error occurred"}</div>);
                setSubmitting(false);
            });
    }

    useEffect(() => {
        if (!!submitting) {
            submit();
        }
    }, [submitting]);

    const reset = () => {
        setStartDate("");
        setEndDate("");
        setLeaveReason("");

        setSubmitting(false);
        setErrors(null);
    }

    const payload = () => {
        let startAt = null;
        if (!!startDate && startDate !== "") {
            startAt = startOfDay(parse(startDate, DATE_FORMATS.DB_DATE, new Date()));
        }

        let endAt = null;
        if (!!endDate && endDate !== "") {
            endAt = endOfDay(parse(endDate, DATE_FORMATS.DB_DATE, new Date()));
        }

        return {
            method: !!userLeaveRequestId ? 'patch' : 'post',
            url:    !!userLeaveRequestId ? route('web-api.user-leave-request.update', {user_leave_request: userLeaveRequestId}).toString() : route('web-api.user-leave-request.store').toString(),
            data: {
                user_id:            userId,
                start_at:           (!!startAt) ? format(startAt, DATE_FORMATS.DB) : "",
                end_at:             (!!endAt) ? format(endAt, DATE_FORMATS.DB) : "",
                leave_reason_id:    leaveReason,
            },
        }
    }

    const submit = () => {
        axios(payload())
            .then((response) => {
                reset();

                if (!!response && !!response.data && !!response.data.double_booked) {
                    toast.warning(doubleBookedHelper(response));
                }

                onConfirm();
            })
            .catch((error) => {
                console.log(error);
                if (!!error.response.data && !!error.response.data.errors) {
                    setErrors(error.response.data.errors);
                    toast.error(<div>{!!error.response.data.message ? error.response.data.message : "An error occurred"}</div>);
                }

                setSubmitting(false);
            });
    }

    const handleClose = () => {
        if (!submitting) {
            reset();
            onCancel();
        }
    }

    const onChange = (field, value) => {
        switch (field) {
            case "start_at":
                setStartDate(value);
                break;
            case "end_at":
                setEndDate(value);
                break;
            case "leave_reason_id":
                setLeaveReason(value);
                break;
        }
    }

    return (
        <Modal
            ariaHideApp={false}
            isOpen={show}
            parentSelector={() => document.getElementById(parentId)}
            onRequestClose={() => handleClose()}
            contentLabel={!!userLeaveRequestId ? "Edit Leave Request" : "Add Leave Request"}
            className={'ReactModal__Content react-modal-outer modal-dialog admin-modal wide-modal allow-overflow'}
            style={{
                overlay: {
                    width: '100vw',
                    height: '100vh',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    overflow: 'hidden',
                    outline: 'none',
                },
                content: {
                    width: "100%",
                    height: "100%",
                    padding: 0,
                    border: 0,
                    outline: 'none',
                },
            }}
        >
            <div className="p-4 modal-content" id={id} style={{minHeight: '60vh'}}>
                <div className="modal-header border-0 m-0 p-0 pb-4">
                    <h4 className="modal-title text-eh-navy">{!!userLeaveRequestId ? "Edit Leave Request" : "Add Leave Request"}</h4>
                    <button
                        type="button" className="close"
                        data-dismiss="modal" aria-label="Close"
                        onClick={() => handleClose()}>
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>
                <div className="modal-body m-0 mb-3 p-0">
                    <div className="row m-0 p-0">
                        <div className="col-12 col-md-6 m-0 p-0 pr-md-2">
                            <div className={"bespoke-input"}>
                                <Input
                                    name={"start_at"}
                                    label={"Start Date"}
                                    required={true}
                                    type={"date"}
                                    value={startDate}
                                    disabled={submitting}
                                    onChange={(value) => onChange("start_at", value)}
                                    error={!!errors && !!errors.start_at ? errors.start_at : null}
                                />
                            </div>
                        </div>
                        <div className="col-12 col-md-6 m-0 p-0 pl-md-2">
                            <div className={"bespoke-input"}>
                                <Input
                                    name={"end_at"}
                                    label={"End Date"}
                                    required={true}
                                    type={"date"}
                                    value={endDate}
                                    disabled={submitting}
                                    onChange={(value) => onChange("end_at", value)}
                                    error={!!errors && !!errors.end_at ? errors.end_at : null}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="row m-0 p-0">
                        <div className="col-12 m-0 p-0 bespoke-input">
                            <Input
                                name={"leave_reason_id"}
                                label={"Reason"}
                                required={true}
                                type={"searchable-select"}
                                options={!!leaveReasons && leaveReasons.length ? leaveReasons : null}
                                value={leaveReason}
                                disabled={submitting}
                                onChange={(value) => onChange("leave_reason_id", value)}
                                error={!!errors && !!errors.leave_reason_id ? errors.leave_reason_id : null}
                            />
                        </div>
                    </div>
                </div>
                <div className="modal-footer m-0 p-0 d-flex justify-content-start align-items-center border-0">
                    <button
                        type="submit"
                        className="btn btn-eh-lilac py-0 px-3 ml-0 py-0 mr-4"
                        onClick={() => setSubmitting(true)}
                        disabled={submitting || disabled}
                    >
                        {!!userLeaveRequestId ? "Save Changes" : "Add Leave Request"}
                    </button>
                    <button
                        type="button" data-dismiss="modal"
                        className="btn btn-outline-eh-off-black px-3 mr-0"
                        onClick={() => handleClose()}
                    >
                        {"Cancel"}
                    </button>
                </div>
            </div>
        </Modal>
    );
}

export default CreateEditUserLeaveRequestModal;
